import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Button, 
  Spin, 
  Tag, 
  Divider, 
  Avatar, 
  Space,
  Modal,
  Form,
  Input,
  Upload,
  Alert
} from 'antd';
import { 
  ArrowLeftOutlined, 
  CalendarOutlined, 
  EnvironmentOutlined, 
  DollarOutlined, 
  ClockCircleOutlined,
  UserOutlined,
  SendOutlined,
  InboxOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/api';
import { JobApplicationRequest } from '../../types';
import { jobPostingQueryKeys } from '../../lib/queryClient';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { isSubscriptionUiActive } from '../../utils/subscriptionUiActive';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const toast = useToast();
  const jobId = id ? parseInt(id) : null;
  
  const isSubscriptionActive = isSubscriptionUiActive(user);
  
  const [applicationModalVisible, setApplicationModalVisible] = useState(false);
  const [applicationForm] = Form.useForm();
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvFileList, setCvFileList] = useState<any[]>([]);

  const { data: job, isLoading: loading, isError } = useQuery({
    queryKey: jobPostingQueryKeys.detail(jobId!),
    queryFn: async () => {
      const response = await apiService.getPublicJobPosting(jobId!);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    enabled: !!jobId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: myApplications = [] } = useQuery({
    queryKey: jobPostingQueryKeys.myApplications,
    queryFn: async () => {
      const response = await apiService.getMyJobApplications();
      if (!response.success) throw new Error(response.message);
      return response.data?.data ?? [];
    },
    enabled: !!user && !!jobId,
    staleTime: 0,
  });

  const hasApplied = jobId ? myApplications.some((app: { jobPostingId: number }) => app.jobPostingId === jobId) : false;

  const applicationMutation = useMutation({
    mutationFn: async (values: JobApplicationRequest) => {
      const formData = new FormData();
      formData.append('CoverLetter', values.CoverLetter);
      if (cvFile) formData.append('ResumeFile', cvFile);
      const response = await apiService.applyToJob(jobId!, formData);
      if (!response.success) throw new Error(response.message);
      return response;
    },
    onSuccess: () => {
      setApplicationModalVisible(false);
      applicationForm.resetFields();
      setCvFile(null);
      setCvFileList([]);
      queryClient.invalidateQueries({ queryKey: jobPostingQueryKeys.myApplications });
      toast.success('Başvurunuz başarıyla gönderildi.');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Başvuru gönderilirken hata oluştu'));
    }
  });

  const handleApplicationSubmit = (values: JobApplicationRequest) => {
    applicationMutation.mutate(values);
  };

  const handleApply = () => {
    if (!user) {
      toast.warning('Başvuru yapmak için giriş yapmalısınız');
      return;
    }
    
    if (user.role !== 'Mentee' && user.role !== 'Mentor') {
      toast.warning('Sadece Mentee ve Mentor kullanıcıları başvuru yapabilir');
      return;
    }
    
    if (!isSubscriptionActive) {
      toast.warning('Başvurabilmeniz için abone olmalısınız');
      const panelPath = user?.role === 'Mentor' ? '/profile?tab=subscription' : 
                       user?.role === 'Mentee' ? '/mentee-profile?tab=subscription' : 
                       '/login';
      navigate(panelPath);
      return;
    }
    
    setApplicationModalVisible(true);
  };

  const handleCvUpload = (info: any) => {
    const { file } = info;
    
    // PDF format kontrolü
    if (file.type !== 'application/pdf') {
      toast.error('Sadece PDF dosyaları kabul edilir');
      return;
    }
    
    // 20MB boyut kontrolü
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      toast.error('Dosya boyutu 20MB\'dan küçük olmalıdır');
      return;
    }
    
    setCvFile(file);
    setCvFileList([file]);
  };

  const handleCvRemove = () => {
    setCvFile(null);
    setCvFileList([]);
  };

  const formatSalary = (min?: number, max?: number) => {
    if (!min && !max) return 'Belirtilmemiş';
    if (min && max) return `${min.toLocaleString()} - ${max.toLocaleString()} TL`;
    if (min) return `${min.toLocaleString()}+ TL`;
    if (max) return `En fazla ${max.toLocaleString()} TL`;
    return 'Belirtilmemiş';
  };

  if (isError) {
    toast.error('İş ilanı bulunamadı');
    navigate('/job-postings');
    return null;
  }

  if (loading) {
    return (
      <div className="app-page">
        <div className="app-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="app-page">
        <div className="app-empty-card">
          <Text>İş ilanı bulunamadı</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="app-container">
        <div style={{ marginBottom: '24px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/job-postings')}>
            Geri Dön
          </Button>
        </div>
          
          <Card className="app-content-card" style={{ marginBottom: '24px' }}>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={16}>
                <div className="flex items-start space-x-4">
                  {job.companyLogo && (
                    <Avatar 
                      size={64} 
                      src={`data:image/png;base64,${job.companyLogo}`}
                      icon={<UserOutlined />}
                    />
                  )}
                  <div className="flex-1">
                    <Title level={2} className="mb-2">{job.title}</Title>
                    <Text strong className="text-lg text-gray-600">{job.companyName || 'Şirket Adı'}</Text>
                    
                    <div className="mt-4">
                      <Space wrap>
                        <Tag icon={<EnvironmentOutlined />} color="blue">
                          {job.location}
                        </Tag>
                        <Tag icon={<UserOutlined />} color="green">
                          {job.department}
                        </Tag>
                        <Tag color="orange">
                          {job.employmentType}
                        </Tag>
                        <Tag color="purple">
                          {job.minExperienceYears != null && job.maxExperienceYears != null
                            ? `${job.minExperienceYears}-${job.maxExperienceYears} yıl`
                            : (job.experienceLevel || 'Belirtilmemiş')}
                        </Tag>
                      </Space>
                    </div>
                  </div>
                </div>
              </Col>
              
              <Col xs={24} lg={8}>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <DollarOutlined className="text-green-600" />
                    <Text strong>{formatSalary(job.salaryMin, job.salaryMax)}</Text>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <ClockCircleOutlined className="text-blue-600" />
                    <Text>Son başvuru: {dayjs(job.applicationDeadline).format('DD MMMM YYYY')}</Text>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <CalendarOutlined className="text-gray-600" />
                    <Text>Yayın tarihi: {dayjs(job.createdAt).format('DD MMMM YYYY')}</Text>
                  </div>
                  
                  {user && user.role === 'Company' ? null : (
                    !user ? (
                      <Alert
                        message="Başvuru yapmak için giriş yapmalısınız"
                        type="info"
                        showIcon
                      />
                    ) : user.role === 'Mentee' || user.role === 'Mentor' ? (
                      hasApplied ? (
                        <Alert
                          message="Bu iş ilanına zaten başvuru yaptınız"
                          type="success"
                          showIcon
                        />
                      ) : !isSubscriptionActive ? (
                        <div style={{ textAlign: 'center' }}>
                          <Alert
                            message="Başvurabilmeniz için abone olmalısınız"
                            type="warning"
                            showIcon
                            style={{ marginBottom: '16px' }}
                          />
                          <Button 
                            type="primary" 
                            size="large" 
                            icon={<CrownOutlined />}
                            onClick={() => {
                              const panelPath = user?.role === 'Mentor' ? '/profile?tab=subscription' : 
                                               user?.role === 'Mentee' ? '/mentee-profile?tab=subscription' : 
                                               '/login';
                              navigate(panelPath);
                            }}
                            block
                          >
                            Abonelik Satın Al
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          type="primary" 
                          size="large" 
                          icon={<SendOutlined />}
                          onClick={handleApply}
                          block
                        >
                          Başvuru Yap
                        </Button>
                      )
                    ) : (
                      <Alert
                        message="Başvuru yapmak için giriş yapmalısınız"
                        type="info"
                        showIcon
                      />
                    )
                  )}
                </div>
              </Col>
            </Row>
          </Card>

        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={16}>
            <Card className="app-content-card" title="İş Tanımı" style={{ marginBottom: 24 }}>
              <div 
                dangerouslySetInnerHTML={{ __html: job.description }}
                className="prose max-w-none"
              />
            </Card>

            <Card className="app-content-card" title="Gereksinimler" style={{ marginBottom: 24 }}>
              <div 
                dangerouslySetInnerHTML={{ __html: job.requirements }}
                className="prose max-w-none"
              />
            </Card>

            {job.benefits && (
              <Card className="app-content-card" title="Yan Haklar" style={{ marginBottom: 24 }}>
                <div 
                  dangerouslySetInnerHTML={{ __html: job.benefits }}
                  className="prose max-w-none"
                />
              </Card>
            )}
          </Col>

          <Col xs={24} lg={8}>
            <Card className="app-content-card" title="Şirket Bilgileri">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  {job.companyLogo && (
                    <Avatar 
                      size={48} 
                      src={`data:image/png;base64,${job.companyLogo}`}
                      icon={<UserOutlined />}
                    />
                  )}
                  <div>
                    <Text strong className="text-lg">{job.companyName}</Text>
                    {job.companyIndustry && (
                      <Text type="secondary" className="block">{job.companyIndustry}</Text>
                    )}
                  </div>
                </div>

                <Divider />

                {job.companyDescription && (
                  <div>
                    <Text strong className="block mb-2">Hakkımızda</Text>
                    <div 
                      dangerouslySetInnerHTML={{ __html: job.companyDescription }}
                      className="prose max-w-none"
                    />
                  </div>
                )}

                {job.companyWebsite && (
                  <div>
                    <Text strong className="block mb-1">Website</Text>
                    <a 
                      href={job.companyWebsite} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {job.companyWebsite}
                    </a>
                  </div>
                )}

                {job.companySize && (
                  <div>
                    <Text strong className="block mb-1">Şirket Büyüklüğü</Text>
                    <Text>{job.companySize}</Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Application Modal */}
        <Modal
          title="İş Başvurusu"
          open={applicationModalVisible}
          onCancel={() => setApplicationModalVisible(false)}
          footer={null}
          width={600}
        >
          <Form
            form={applicationForm}
            layout="vertical"
            onFinish={handleApplicationSubmit}
          >
            <Form.Item
              name="CoverLetter"
              label="Ön Yazı"
              rules={[{ required: true, message: 'Ön yazı gereklidir' }]}
            >
              <TextArea
                rows={6}
                placeholder="Kendinizi tanıtın ve neden bu pozisyon için uygun olduğunuzu açıklayın..."
                maxLength={2000}
                showCount
              />
            </Form.Item>

            <Form.Item
              label="CV Yükle (PDF)"
              help="Maksimum 20MB, sadece PDF formatı kabul edilir"
            >
              <Upload.Dragger
                name="cv"
                fileList={cvFileList}
                beforeUpload={() => false}
                onChange={handleCvUpload}
                onRemove={handleCvRemove}
                accept=".pdf"
                maxCount={1}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">CV dosyanızı buraya sürükleyin veya tıklayarak seçin</p>
                <p className="ant-upload-hint">
                  PDF formatında, maksimum 20MB boyutunda dosya yükleyin
                </p>
              </Upload.Dragger>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={applicationMutation.isPending}
                  icon={<SendOutlined />}
                >
                  Başvuru Gönder
                </Button>
                <Button onClick={() => setApplicationModalVisible(false)}>
                  İptal
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default JobDetail;
